import {
  AttachType,
  DialogModel,
  ListInfoModel,
  MessageModel,
  MessageReplyToModel,
  TelegramChat,
  TipsModel,
  UserModel,
} from '$store/models';
import { PayloadAction } from '@reduxjs/toolkit';
import { ReportReason } from '$shared/constants/reportReason';
import { LanguageTags } from '$shared/constants';

export type MessagesMap = { [index: number]: MessageModel };

export interface ServiceAccountContext {
  asPeerId?: number;
}

export type WelcomeMessageData = {
  loadingStatus: 'loading' | 'loaded' | 'failed';
  text: string;
  price: string;
  priceWithoutDiscount: string;
  isPaid: boolean;
  isEnabled: boolean;
};

export enum BroadcastListEnum {
  PAID_MESSAGES = 'paidMessages',
  SUBSCRIBERS = 'subscribers',
  FOLLOWINGS = 'followings',
  SPONSORS = 'sponsors',
  TELEGRAM = 'telegram',
  TIPS = 'tips',
  REFERRALS = 'referrals',
}

export interface BroadcastUser {
  id: number;
  disabled: boolean;
  selected: boolean;
}

export enum BroadcastStatsStatusType {
  OLD = 'old',
  PROCESSING = 'processing',
  FINISHED = 'finished',
  STOPPED = 'stopped',
  ROLLBACK = 'rollback',
  ROLLBACK_FINISHED = 'rollback_finished',
  UNKNOWN = 'unknown',
}

export interface BroadcastStatsItem {
  attachments: AttachType[];
  createdAt: number;
  id: number;
  price: number;
  purchasedCount: number;
  recipientsCount: number;
  deliveredCount: number;
  seenCount: number;
  text: string;
  unsent: boolean;
  status: BroadcastStatsStatusType;
}

export interface BroadcastList {
  count: number;
  listId: BroadcastListEnum;
  participants: number[];
  title: string;
}

export type FilterDateRange = [Date, Date];

export enum BroadcastFilterName {
  LANGUAGES = 'languages',
  SUBSCRIBE_DATE = 'subscribeDate',
  REGISTRATION_DATE = 'registrationDate',
  SPONSOR_SUBSCRIBE_DATE = 'sponsorSubscribeDate',
}

export interface BroadcastFilters {
  languages: LanguageTags[];
  subscribeDate: FilterDateRange;
  sponsorSubscribeDate: FilterDateRange;
  registrationDate: FilterDateRange;
}

export type ChooseChatTelegramSheetType = 'post' | 'connect';

export interface BroadcastData {
  totalRecipientsCount: number;
  lists: BroadcastList[];
  activeFilters: { [key in BroadcastFilterName]?: boolean };
  appliedActiveFilters: { [key in BroadcastFilterName]?: boolean };
  filters: BroadcastFilters;
  appliedFilters: BroadcastFilters;
  isListsLoading: boolean;
  isRecipientsLoading: boolean;
  searchText: string;
  sendTo: BroadcastListEnum[];
  exclude: BroadcastListEnum[];
  users: BroadcastUser[];
  followerIds: number[];
  telegramIds: number[];
  eroticSubscribers: number[];
  recipientIds: number[];
  sponsorIds: number[];
  userIds: number[];
  excludeUserIds: number[];
  userNames: { [index: number]: string[] };
  skipIds: number[];
  text: string;
  price: string;
  priceWithoutDiscount: string;
  rollbackPrevious: boolean;
  skipUpToTopDialogs: boolean;
  isPaid: boolean;
}

export interface BroadcastStatsData {
  isLoading: boolean;
  isLoadingMore: boolean;
  nextFrom: string;
  broadcasts: BroadcastStatsItem[];
}

export enum CallStatus {
  CONNECTING = 'connecting',
  CALLING = 'calling',
  RINGING = 'ringing',
  CANCELED = 'canceled',
  REJECTED = 'rejected',
  CONNECTED = 'connected',
  COMPLETED = 'completed',
}

export enum CallType {
  INCOMING = 'incoming',
  OUTGOING = 'outgoing',
}

export enum WelcomeMessageType {
  FOLLOWER = 'follower',
  SPONSOR = 'sponsor',
  TG_SUBSCRIBER = 'tg_subscriber',
}

export interface ImState {
  isDialogsLoading: boolean;
  isDialogsLoadingMore: boolean;
  lists: ListInfoModel[];
  nextFrom: string;
  dialogsList: number[];
  dialogs: { [index: number]: DialogModel };
  history: { [index: number]: MessagesMap };
  typing: { [index: number]: boolean };
  info: { [index: number]: ImChatInfo };
  isWriteLoading: boolean;
  writeIds: number[];
  spendLevel: number;
  serviceAccounts: number[];
  isServiceAccountsLoading: boolean;
  asPeerId: number;
  forms: {
    [index: string]: {
      message: string;
      price: string;
      priceWithoutDiscount: string;
      lang?: string;
      currency?: string;
    };
  };
  broadcastStats: BroadcastStatsData;
  tonRate: number | null;
  broadcast: BroadcastData;
  telegram: {
    isChatsLoading: boolean;
    isTelegramSheetVisible: boolean;
    isChooseChatTelegramSheetVisible: boolean;
    chooseChatTelegramSheetType: ChooseChatTelegramSheetType;
    chats: TelegramChat[];
    chatIds: number[];
  };
  welcomeMessage: {
    [WelcomeMessageType.FOLLOWER]: WelcomeMessageData;
    [WelcomeMessageType.SPONSOR]: WelcomeMessageData;
    [WelcomeMessageType.TG_SUBSCRIBER]: WelcomeMessageData;
  };
  isVideoUploading: boolean;
  section: string;
  suggestions: number[];
  avgInvoicePrice: number | null;
  call: {
    type: CallType | null;
    status: CallStatus | null;
    peerId: number;
    remoteStream: MediaStream | null;
    localStream: MediaStream | null;
    remoteDescription: RTCSessionDescription | null;
    callId: string | null;
    iceServers: IceServer[] | null;
  };
  hasWelcomeMessage: boolean;
  showSubscriptionRequiredMessage: { [index: number]: boolean };
  unAnsweredCount: number;
  aiOptions: string[];
}

export interface ImChatInfo {
  text: string;
  isLoading: boolean;
  isLoadingMore: boolean;
  nextFrom: string;
  isFailed: boolean;
  failReason: string;
  isSponsor?: boolean;
  replyTo?: MessageReplyToModel | null;
}

export type SetBroadcastSendToAction = PayloadAction<BroadcastListEnum[]>;
export type SetBroadcastExcludeAction = PayloadAction<BroadcastListEnum[]>;
export type SendBroadcastTotalRecipientsCountAction = PayloadAction<number>;

export type SetBroadcastListAction = PayloadAction<BroadcastList[]>;
export type SetIsListsLoadingAction = PayloadAction<boolean>;
export type SetBroadcastRecipientAction = PayloadAction<UserModel[]>;
export type SetBroadcastAction = PayloadAction<{
  userIds: number[];
  followerIds: number[];
  telegramIds: number[];
  sponsorIds: number[];
  skipIds: number[];
  eroticSubscribers: number[];
  users: { [index: number]: UserModel };
}>;

export type SetBroadcastSearchTextAction = PayloadAction<string>;
export type SetBroadcastMessageTextAction = PayloadAction<string>;
export type SetBroadcastPriceAction = PayloadAction<string>;
export type SetBroadcastPaidAction = PayloadAction<boolean>;
export type SetBroadcastRollbackPreviousAction = PayloadAction<boolean>;
export type SetBroadcastSkipUpToTopDialogsAction = PayloadAction<boolean>;
export type SetBroadcastSkipIdsAction = PayloadAction<number[]>;
export type SetBroadcastFilterActiveAction = PayloadAction<{
  filterName: BroadcastFilterName;
  isActive: boolean;
}>;
export type SetBroadcastFilterLanguageAction = PayloadAction<LanguageTags[]>;
export type SetBroadcastFilterDateAction = PayloadAction<FilterDateRange>;
export type SetBroadcastExcludeUserIdsAction = PayloadAction<number[]>;
export type SetBroadcastExcludeUserIdAction = PayloadAction<{
  id: number;
  value: boolean;
}>;
export type SetBroadcastStatsLoadingAction = PayloadAction<boolean>;
export type LoadBroadcastStats = PayloadAction<string>;
export type SetMoreBroadcastStats = PayloadAction<{
  nextFrom: string;
  broadcasts: BroadcastStatsItem[];
}>;
export type UnsendBroadcastAction = PayloadAction<number>;
export type SetUnsentBroadcastAction = PayloadAction<{
  id: number;
  unsent: boolean;
}>;
export type SetBroadcastStatsAction = PayloadAction<{
  isLoading?: boolean;
  isLoadingMore?: boolean;
  nextFrom?: string;
  broadcasts?: BroadcastStatsItem[];
}>;
export type SetDialogsAction = PayloadAction<{
  dialogs: DialogModel[];
  messages: { [index: number]: MessageModel };
  nextFrom?: string;
  section?: string;
  suggestions?: number[];
}>;
export type LoadDialogsAction = PayloadAction<ServiceAccountContext | undefined>;
export type LoadChatAction = PayloadAction<{ peerId: number } & ServiceAccountContext>;
export type LoadMoreDialogsAction = PayloadAction<
  { nextFrom: string } & ServiceAccountContext
>;
export type ChatSetFormTextAction = PayloadAction<{ peerId: number; text: string }>;
export type ChatSetReplyToAction = PayloadAction<{
  peerId: number;
  replyTo: MessageReplyToModel | null;
}>;
export type SendMessageAction = PayloadAction<{ peerId: number } & ServiceAccountContext>;
export type SendInstantMessageAction = PayloadAction<
  { peerId: number; text: string } & ServiceAccountContext
>;
export type LoadMoreHistoryAction = PayloadAction<
  { peerId: number; nextFrom: string } & ServiceAccountContext
>;
export type LoadWriteAction = PayloadAction<ServiceAccountContext | undefined>;
export type SetTypingAction = PayloadAction<{ peerId: number; typing: boolean }>;
export type ResetTypingAction = PayloadAction<number>;
export type SendTypingAction = PayloadAction<{ peerId: number } & ServiceAccountContext>;
export type SetHistoryAction = PayloadAction<{
  peerId: number;
  history: MessagesMap;
  spendLevel?: number;
  nextFrom?: string;
  replace?: boolean;
}>;
export type SetChatFailedAction = PayloadAction<{ peerId: number; reason: string }>;
export type SetMessageAction = PayloadAction<{
  peerId: number;
  messageId: number;
  message: MessageModel;
}>;
export type RetrySendAction = PayloadAction<
  { peerId: number; message: MessageModel } & ServiceAccountContext
>;
export type ReadHistoryAction = PayloadAction<{ peerId: number } & ServiceAccountContext>;
export type SetWriteIdsAction = PayloadAction<number[]>;
export type DeleteDialogAction = PayloadAction<
  { peerId: number } & ServiceAccountContext
>;
export type RemoveDialogAction = PayloadAction<number>;
export type DeleteMessageAction = PayloadAction<
  { id: number; peerId: number } & ServiceAccountContext
>;
export type ReportMessageAction = PayloadAction<{
  messageId: number;
  reason: ReportReason;
}>;
export type CreateInvoiceAction = PayloadAction<{
  peerId: number;
  price: number;
  priceWithoutDiscount: number;
  text: string;
  currency: string;
}>;

export type BuyMessageAction = PayloadAction<{
  peerId: number;
  message: MessageModel;
}>;
export type SendTipsAction = PayloadAction<TipsModel>;
export type MarkMessageAsBoughtAction = PayloadAction<{
  peerId: number;
  messageId: number;
}>;
export type UpdateFormMessageAction = PayloadAction<{ formId: string; message: string }>;
export type UpdateFormLangAction = PayloadAction<{ formId: string; lang: string }>;
export type UpdateFormCurrencyAction = PayloadAction<{
  formId: string;
  currency: string;
}>;
export type UpdateFormPriceAction = PayloadAction<{ formId: string; price: string }>;
export type UpdateFormPriceWithoutDiscountAction = PayloadAction<{
  formId: string;
  priceWithoutDiscount: string;
}>;
export type SendTipFromBalanceAction = PayloadAction<{
  tipsId: number;
  skipAutoPay?: boolean;
}>;
export type SetTonRateAction = PayloadAction<number>;
export type EditWelcomeMessageAction = PayloadAction<WelcomeMessageType>;
export type LoadWelcomeMessageAction = PayloadAction<WelcomeMessageType>;
export type SetWelcomeMessageLoadingAction = PayloadAction<{
  type: WelcomeMessageType;
  loading: WelcomeMessageData['loadingStatus'];
}>;
export type SetWelcomeMessageAction = PayloadAction<{
  type: WelcomeMessageType;
  data: WelcomeMessageData;
}>;
export type ClearFormAction = PayloadAction<string>;
export type SetIsVideoUploadingAction = PayloadAction<boolean>;
export type SetCallTypeAction = PayloadAction<CallType | null>;
export type SetCallStatusAction = PayloadAction<CallStatus | null>;
export type SetCallPeerIdAction = PayloadAction<number>;
export interface IceServer {
  urls: string[];
  username?: string;
  credential?: string;
}

export type CallAction = PayloadAction<{
  peerId: number;
  description: string;
}>;
export type IncomingCallAction = PayloadAction<{
  peerId: number;
  remoteDescription: RTCSessionDescription;
  callId?: string;
  iceServers?: IceServer[];
}>;
export type SetCallRemoteDescription = PayloadAction<RTCSessionDescription | null>;
export type CallSendAcceptAction = PayloadAction<RTCSessionDescription | null>;
export type CallCancelAction = PayloadAction<number>;
export type TelegramRetryAction = PayloadAction<{
  messageId: number;
  peerId: number;
}>;
export type LoadAvgInvoicePriceAction = PayloadAction<string>;
export type SetAvgInvoicePriceAction = PayloadAction<number>;
export type SetHasWelcomeMessageAction = PayloadAction<boolean>;
export type SetTelegramSheetVisibleAction = PayloadAction<boolean>;
export type SetTelegramChatsAction = PayloadAction<TelegramChat[]>;
export type SetTelegramChatIdsAction = PayloadAction<number[]>;
export type SetChooseChatTelegramSheetVisibleAction = PayloadAction<boolean>;
export type ShowChooseChatTelegramSheetWithTypeAction =
  PayloadAction<ChooseChatTelegramSheetType>;
export type SetShowSubscriptionRequiredMessageAction = PayloadAction<{
  peerId: number;
  show: boolean;
}>;
export type SendValentineDayStickerAction = PayloadAction<number>;
export type SetUnAnsweredCountAction = PayloadAction<number>;
export type SetServiceAccountsAction = PayloadAction<number[]>;
export type SetAsPeerIdAction = PayloadAction<number>;
export type LoadServiceAccountsAction = PayloadAction<undefined>;
